import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ProjectStatus } from '../../../layouts/my-projects/project.const';
import { TranslateService } from '../../services/translate.service';
import {
  PROJECT_DROPDOWN_OPTIONS,
  PROJECT_STATUS_OPTIONS,
  STATUS_VALUE_ALIASES,
  StatusOption,
  UNKNOWN_STATUS_OPTION,
} from '../status-badge/status.const';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

@Component({
  selector: 'mtm-status-select',
  standalone: true,
  imports: [CommonModule, OverlayModule, StatusBadgeComponent, LoadingSpinnerComponent],
  templateUrl: './status-select.component.html',
  styleUrl: './status-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusSelectComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly translate = inject(TranslateService);

  readonly value = input<ProjectStatus | string | null>(null);
  readonly options = input<StatusOption[]>(PROJECT_DROPDOWN_OPTIONS);
  readonly disabled = input<boolean>(false);
  readonly isLoading = input<boolean>(false);
  // Badge shown when value is null or doesn't match any option. Default = UNKNOWN.
  readonly fallbackOption = input<StatusOption>(UNKNOWN_STATUS_OPTION);

  readonly valueChange = output<StatusOption>();

  readonly isOpen = signal(false);
  readonly scrollStrategy = this.overlay.scrollStrategies.close();

  readonly selectedOption = computed<StatusOption>(() => {
    const v = this.value();
    if (!v) return this.fallbackOption();
    // Normalize alias forms (e.g. asset-list 'TO_DO' → ProjectStatus.ToDo 'TO_COME').
    const normalized = STATUS_VALUE_ALIASES[v] ?? v;
    // 1) Match in the dropdown's selectable options.
    const fromOptions = this.options().find((o) => o.value === normalized);
    if (fromOptions) return fromOptions;
    // 2) Display-only match against the full project vocabulary (e.g. asset with status TO_COME
    //    — render the To Do badge even though it's not selectable here).
    const fromAll = PROJECT_STATUS_OPTIONS.find((o) => o.value === normalized);
    if (fromAll) return fromAll;
    // 3) Unknown / not-affected fallback.
    return this.fallbackOption();
  });

  readonly selectedLabel = computed<string>(() => this.translateLabel(this.selectedOption()));

  readonly headerLabel = computed<string>(() => this.translate.data?.['select_status'] || 'Select Status');

  private isScrollListenerAttached = false;
  private readonly onDocumentScroll = (event: Event): void => {
    if (!this.isOpen()) return;
    const target = event.target as Element | null;
    if (target?.closest('.dropdown-panel')) return;
    this.close();
  };

  translateLabel(option: StatusOption): string {
    // UPCOMING_EXPIRY ships translated as "Upcoming expiry" but the status badge/dropdown
    // displays it as just "Upcoming".
    if (option.value === ProjectStatus.UpcomingExpiry) return option.statusName;
    const key = `task_${option.value}`;
    return this.translate.data?.[key] || option.statusName || key;
  }

  toggle(): void {
    if (this.disabled()) return;
    const next = !this.isOpen();
    this.isOpen.set(next);
    this.setScrollListener(next);
  }

  close(): void {
    this.setScrollListener(false);
    this.isOpen.set(false);
  }

  select(option: StatusOption): void {
    this.valueChange.emit(option);
    this.close();
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
  }

  private setScrollListener(active: boolean): void {
    if (typeof document === 'undefined') return;
    if (active && !this.isScrollListenerAttached) {
      document.addEventListener('scroll', this.onDocumentScroll, true);
      this.isScrollListenerAttached = true;
      return;
    }
    if (!active && this.isScrollListenerAttached) {
      document.removeEventListener('scroll', this.onDocumentScroll, true);
      this.isScrollListenerAttached = false;
    }
  }

  ngOnDestroy(): void {
    this.setScrollListener(false);
  }
}
